import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useCountUp } from "@/hooks/useCountUp";
import { cite, badgeLabel } from "@/data/research-sources";

const StatCard = ({ end, suffix, description, source, badge }: {
  end: number;
  suffix: string;
  description: string;
  source: string;
  badge: string;
}) => {
  const { count, ref } = useCountUp({ end, duration: 1500, threshold: 0.3 });

  return (
    <div className="border-2 border-border bg-card p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
      <p ref={ref as React.RefObject<HTMLParagraphElement>} className="font-mono text-5xl md:text-6xl font-bold text-foreground mb-2">
        {count}{suffix}
      </p>
      <p className="text-sm text-muted-foreground leading-relaxed mb-2">
        {description}
      </p>
      <div className="space-y-0.5">
        <span className="inline-block text-xs font-mono px-2 py-0.5 border border-border text-muted-foreground bg-muted/50">
          {badge}
        </span>
        <p className="text-xs font-serif italic text-muted-foreground">
          {source}
        </p>
      </div>
    </div>
  );
};

const stats = [
  {
    end: 67,
    suffix: "%",
    description: "of all AI citations go to just 20 domains. The rest compete for a fraction of visibility.",
    source: cite('yang-2025'),
    badge: badgeLabel('yang-2025'),
  },
  {
    end: 7,
    suffix: "×",
    description: "Traditional SEO outperforms GEO-only tactics by a factor of seven. SEO is still the foundation.",
    source: cite('neurips-2025'),
    badge: badgeLabel('neurips-2025'),
  },
  {
    end: 40,
    suffix: "%",
    description: "Visibility improvement possible when content is optimized specifically for AI platforms.",
    source: cite('kdd-2024'),
    badge: badgeLabel('kdd-2024'),
  },
];

const WhyMatters = () => {
  const ref = useScrollReveal();
  const gridRef = useScrollReveal<HTMLDivElement>({ stagger: true });

  return (
    <section ref={ref} className="py-10 md:py-16 px-4 bg-secondary/30">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
          The AI visibility gap is growing
        </h2>

        <div ref={gridRef} className="grid md:grid-cols-3 gap-6">
          {stats.map((stat) => (
            <StatCard key={stat.suffix + stat.end} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyMatters;
